import { useMemo } from "react";
import { useMachine } from "react-robot";
import { createMachine, guard, immediate, reduce, state } from "robot3";

import { NUM_INITIAL_BUDGETING_GAME_QUESTIONS } from "src/constants";

import { BUDGET_GAME_STATES, initialContext } from "./constants";
import {
  hasCurrentBudgetCategory,
  isBudgetComplete,
  isInitialDataLoaded,
  isPlanCreated,
  isPlanNotFound,
} from "./guards";
import { completeReducer, slideChangedReducer } from "./reducers";
import {
  calculatorCompletedTransition,
  dataLoadedTransition,
  navigateBudgetingTransition,
  navigateToCalculatorTransition,
  navigateToCompleteTransition,
  optionAddedTransition,
  planCreationFailedTransition,
  regionalTaxRateChangedTransition,
  slideChangedTransition,
  startOverTransition,
} from "./transitions";

const createBudgetGameMachine = (slideConfigs) =>
  createMachine(
    {
      [BUDGET_GAME_STATES.initializing]: state(
        immediate(BUDGET_GAME_STATES.initialized, guard(isInitialDataLoaded)),
        dataLoadedTransition(BUDGET_GAME_STATES.initializing),
      ),
      [BUDGET_GAME_STATES.initialized]: state(
        immediate(
          BUDGET_GAME_STATES.complete,
          guard(isBudgetComplete),
          completeReducer,
        ),
        immediate(
          BUDGET_GAME_STATES.budgeting,
          guard(hasCurrentBudgetCategory),
          reduce((ctx) => {
            const categoryCode = ctx.budgetPlan.currentBudgetCategory.code;
            const slideIndex =
              slideConfigs.findIndex(
                (slide) => slide.budgetCategoryCode === categoryCode,
              ) + NUM_INITIAL_BUDGETING_GAME_QUESTIONS;

            return slideChangedReducer.fn(ctx, {
              slideIndex,
            });
          }),
        ),
        immediate(
          BUDGET_GAME_STATES.calculator,
          guard(isPlanCreated),
          reduce((ctx) => ({
            ...ctx,
            currentSlideIndex: 0,
          })),
        ),
        immediate(BUDGET_GAME_STATES.creatingPlan, guard(isPlanNotFound)),
      ),
      [BUDGET_GAME_STATES.creatingPlan]: state(
        dataLoadedTransition(BUDGET_GAME_STATES.initialized),
        planCreationFailedTransition,
      ),
      [BUDGET_GAME_STATES.planCreationFailed]: state(startOverTransition),
      [BUDGET_GAME_STATES.calculator]: state(
        regionalTaxRateChangedTransition,
        calculatorCompletedTransition,
        slideChangedTransition,
        navigateBudgetingTransition,
        dataLoadedTransition(BUDGET_GAME_STATES.calculator),
      ),
      [BUDGET_GAME_STATES.budgeting]: state(
        slideChangedTransition,
        optionAddedTransition(),
        navigateToCalculatorTransition,
        navigateToCompleteTransition,
        navigateBudgetingTransition,
        dataLoadedTransition(BUDGET_GAME_STATES.budgeting),
      ),
      [BUDGET_GAME_STATES.complete]: state(
        slideChangedTransition,
        startOverTransition,
        navigateBudgetingTransition,
        dataLoadedTransition(BUDGET_GAME_STATES.complete),
        optionAddedTransition(BUDGET_GAME_STATES.complete),
      ),
    },
    () => ({
      ...initialContext,
      slideConfigs,
      totalSlides:
        slideConfigs.length + NUM_INITIAL_BUDGETING_GAME_QUESTIONS + 1,
    }),
  );

export const useBudgetGameState = (slideConfigs) => {
  const machine = useMemo(
    () => createBudgetGameMachine(slideConfigs),
    [slideConfigs],
  );
  const [state, send] = useMachine(machine);

  const { context } = state;

  const senders = useMemo(() => {
    const dataLoaded = (property) => (data) =>
      send({
        type: "dataLoaded",
        property,
        data,
      });

    return {
      budgetPlanLoaded: dataLoaded("budgetPlan"),
      budgetCategoriesLoaded: dataLoaded("budgetCategories"),
      calculatorCompleted: () => send({ type: "calculatorCompleted" }),
      slideChanged: ({ slideIndex, currentBudgetCategory } = {}) =>
        send({ type: "slideChanged", slideIndex, currentBudgetCategory }),
      optionAdded: (budgetPlan) => send({ type: "optionAdded", budgetPlan }),
      planCreationFailed: (planCreationError) =>
        send({ type: "planCreationFailed", planCreationError }),
      regionalTaxRateChanged: (regionalIncomeTaxRate) =>
        send({ type: "regionalTaxRateChanged", regionalIncomeTaxRate }),
      startOver: () => send({ type: "startOver" }),
      // Navigation actions
      goToNextSlide: () => send({ type: "navigate", direction: "next" }),
      goToPreviousSlide: () =>
        send({ type: "navigate", direction: "previous" }),
      goToSlide: (slideIndex) => send({ type: "navigate", slideIndex }),
    };
  }, [send]);

  // Derived values
  const currentSlideConfig = useMemo(() => {
    const { currentSlideIndex } = context;
    const slideConfigIndex =
      currentSlideIndex - NUM_INITIAL_BUDGETING_GAME_QUESTIONS;

    if (slideConfigIndex >= 0 && slideConfigIndex < slideConfigs.length) {
      return slideConfigs[slideConfigIndex];
    }
  }, [context, slideConfigs]);

  const budgetPlanOptionsByBudgetCategoryCode = useMemo(() => {
    const options = context.budgetPlan?.options ?? [];
    const budgetCategories = context.budgetCategories ?? {};

    const budgetCategoriesById = Object.values(budgetCategories).reduce(
      (acc, category) => {
        acc[category.id] = category;
        return acc;
      },
      {},
    );

    return options.reduce((acc, option) => {
      const categoryCode =
        budgetCategoriesById[option.budgetCategoryOption.budgetCategoryId]
          ?.code;
      if (categoryCode) {
        acc[categoryCode] = option;
      }
      return acc;
    }, {});
  }, [context.budgetPlan?.options, context.budgetCategories]);

  const isLastSlide = context.currentSlideIndex === context.totalSlides - 1;
  const isFirstSlide = context.currentSlideIndex === 0;

  const canAdvance = useMemo(() => {
    // On the calculator slide, can advance if take home pay is calculated or options exist
    if (isFirstSlide) {
      return (
        context.isTakeHomePayCalculated ||
        (context.budgetPlan?.options?.length ?? 0) > 0
      );
    }

    // On budget category slides, can advance if the current category has a selection
    if (currentSlideConfig) {
      return !!budgetPlanOptionsByBudgetCategoryCode[
        currentSlideConfig.budgetCategoryCode
      ];
    }

    // On the summary slide, can always view pathway if all options selected and budget is positive
    if (isLastSlide) {
      const allOptionsSelected =
        (context.budgetPlan?.options?.length ?? 0) >= slideConfigs.length;
      const netIncome = context.budgetPlan?.taxInfo?.netIncome ?? 0;
      const costs = context.budgetPlan?.budgetTotals?.costs ?? 0;
      return allOptionsSelected && netIncome / 12 - costs >= 0;
    }

    return false;
  }, [
    isFirstSlide,
    isLastSlide,
    context.isTakeHomePayCalculated,
    context.budgetPlan,
    currentSlideConfig,
    budgetPlanOptionsByBudgetCategoryCode,
    slideConfigs.length,
  ]);

  const canViewSummary = useMemo(() => {
    return (context.budgetPlan?.options?.length ?? 0) >= slideConfigs.length;
  }, [context.budgetPlan?.options?.length, slideConfigs.length]);

  return {
    state,
    context,
    ...senders,
    // Derived values
    currentSlideConfig,
    budgetPlanOptionsByBudgetCategoryCode,
    canAdvance,
    canViewSummary,
    isFirstSlide,
    isLastSlide,
  };
};
